import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

/**
 * Portals a modal/backdrop subtree to <body>.
 *
 * The three shell columns are `position: sticky` scrollers, and sticky
 * ALWAYS creates a stacking context — so a `position: fixed` backdrop
 * rendered inside a column is trapped at that column's layer no matter its
 * z-index, and the editor column (a later positioned sibling) paints on top
 * of the open modal. No z-index on the backdrop can fix that from inside;
 * the node has to leave the column. Every backdrop-style overlay in the
 * sections must render through this.
 *
 * Nested overlays (a confirm dialog inside an open trend modal) only need
 * the OUTERMOST layer portalled — children ride along.
 *
 * Body scroll lock. `overscroll-behavior: contain` on individual scroll
 * containers only stops chain-at-boundary — a wheel over the dim scrim
 * or any non-scrolling area still walks up to <body>. Every ModalPortal
 * mount claims the lock; the last one to unmount restores the previous
 * body overflow. Reference-counted at module scope so nested modals
 * (trend → correction, trend → truncated-text) don't step on each other:
 * body stays locked while ANY modal is up.
 */
let openCount = 0;
let savedBodyOverflow = null;

/* The wrapper carries the module's scope into <body>: the reset in host.scss
   is keyed off .phr-host, which the portalled subtree has left behind. A plain
   div creates no containing block, so `position: fixed` children are
   unaffected. NOT applied to the Select/DatePicker menus, which sections.scss
   targets as `body > .zy-select-menu` — those are listed in host.scss instead.
   It is also why `React` stays imported here while phr's copy dropped it: phr
   portals `children` directly and needs no JSX in this file. */
const ModalPortal = ({ children }) => {
  useEffect(() => {
    if (openCount === 0) {
      savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openCount += 1;
    return () => {
      openCount -= 1;
      if (openCount === 0) {
        document.body.style.overflow = savedBodyOverflow || "";
        savedBodyOverflow = null;
      }
    };
  }, []);

  return ReactDOM.createPortal(<div className="phr-portal">{children}</div>, document.body);
};

ModalPortal.propTypes = {
  children: PropTypes.node,
};

export default ModalPortal;
